// client/src/components/doctor/layout/DoctorLayout.jsx - WITH DYNAMIC TOOLTIP POSITIONING
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTenant } from '../../../context/TenantContext';
import doctorService from '../../../services/doctorService';
import '../../../styles/components/doctor/DoctorLayout.css';

// Import original icons
import logoImage from '../../../assets/images/Neurolex_Logo_New.png';
// 🧩 SVG Icons as React Components
import { ReactComponent as DashboardIcon } from '../../../assets/icons/dashboard_icon.svg';
import { ReactComponent as PatientsIcon } from '../../../assets/icons/UserManagement_Icon.svg';
import { ReactComponent as JournalIcon } from '../../../assets/icons/journal_icon.svg';
import { ReactComponent as AppointmentsIcon } from '../../../assets/icons/appointment_icon.svg';
import { ReactComponent as MessageIcon } from '../../../assets/icons/messages_icon.svg';
import { ReactComponent as MoodCheckInIcon } from '../../../assets/icons/mood_icon.svg';


// 🔧 PERFORMANCE: Move allItems outside component to prevent re-creation
const ALL_MENU_ITEMS = [
  { 
    id: 'dashboard', 
    label: 'Dashboard', 
    path: '/doctor', 
    icon: DashboardIcon,
    feature: 'Dashboard',
    alwaysShow: true,
    implemented: true
  },
  { 
    id: 'patients', 
    label: 'Patients', 
    path: '/doctor/patients', 
    icon: PatientsIcon,
    feature: 'Patients',
    implemented: true
  },
  { 
    id: 'patient-mood-checkins', 
    label: 'Patient Mood Check-Ins', 
    path: '/doctor/mood-checkins', 
    icon: MoodCheckInIcon,
    feature: 'Patient Mood Check-Ins',
    implemented: true
  },
  { 
    id: 'patient-journal', 
    label: 'Patient Journal Management', 
    path: '/doctor/journal-entries', 
    icon: JournalIcon,
    feature: 'Patient Journal Management',
    implemented: true
  },
  { 
    id: 'appointments', 
    label: 'Appointments', 
    path: '/doctor/appointments', 
    icon: AppointmentsIcon,
    feature: 'Appointments',
    implemented: true
  },
  { 
    id: 'messages', 
    label: 'Messages', 
    path: '/doctor/messages', 
    icon: MessageIcon,
    feature: 'Messages',
    implemented: true
  }
];


const DoctorLayout = () => {
  const navigate = useNavigate();
  
  const { 
    isFeatureEnabled,
    tenantSettings,
    getThemeStyles,
    platformName, 
    isLoading
  } = useTenant();
  
  const [currentUser, setCurrentUser] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  
  // ✅ NEW: Tooltip state for dynamic positioning
  const [hoveredItem, setHoveredItem] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0 });

  // 🚨 LOGO SAFEGUARDS: Prevent infinite updates
  const logoErrorRef = useRef(false);
  const logoLoadedRef = useRef(false);
  const lastLogoUrlRef = useRef('');
  
  // 🔧 SMART LOGO: Memoized logo URL with change detection
  const logoData = useMemo(() => {
    const theme = getThemeStyles();
    const currentLogoUrl = theme?.logo || '';
    
    // Only create new cache buster when URL actually changes
    if (currentLogoUrl !== lastLogoUrlRef.current) {
      console.log('🖼️ [Logo] URL changed:', lastLogoUrlRef.current, '->', currentLogoUrl);
      lastLogoUrlRef.current = currentLogoUrl;
      logoErrorRef.current = false; // Reset error state on URL change
      logoLoadedRef.current = false; // Reset loaded state
    }
    
    return {
      url: currentLogoUrl,
      hasLogo: !!currentLogoUrl && !logoErrorRef.current,
      cacheBuster: currentLogoUrl ? Math.floor(Date.now() / 300000) : 0 // Cache for 5 minutes
    };
  }, [getThemeStyles]);

  // 🔧 SMART LOGO: Generate stable logo source
  const logoSrc = useMemo(() => {
    if (!logoData.url || logoErrorRef.current) {
      console.log('🖼️ [Logo] Using fallback logo');
      return logoImage;
    }
    
    // Only add cache buster if we haven't loaded this URL before
    const src = logoLoadedRef.current ? 
      logoData.url : 
      `${logoData.url}?v=${logoData.cacheBuster}`;
    
    console.log('🖼️ [Logo] Generated source:', src);
    return src;
  }, [logoData]);

  // 🔧 LOGO EVENT HANDLERS: Prevent error loops
  const handleLogoLoad = (e) => {
    console.log('✅ [Logo] Loaded successfully:', e.target.src);
    logoLoadedRef.current = true;
    logoErrorRef.current = false;
  };

  const handleLogoError = (e) => {
    console.warn('❌ [Logo] Failed to load:', e.target.src);
    logoErrorRef.current = true;
    
    // Fallback to default logo
    if (e.target.src !== logoImage) {
      console.log('🔄 [Logo] Falling back to default logo');
      e.target.src = logoImage;
    }
  };

  // ✅ NEW: Handle tooltip positioning
  const handleMouseEnter = (e, itemId) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({ top: rect.top + rect.height / 2 });
    setHoveredItem(itemId);
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  // 🔧 USER PROFILE: Load user info without any logo dependency
  const loadDoctorInfo = async () => {
    try {
      setProfileLoading(true);
      
      const userData = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        setCurrentUser(user);
      }
      
      try {
        const response = await doctorService.getProfile();
        let profileData = {};
        if (response.success && response.data) {
          profileData = response.data;
        } else if (response.data) {
          profileData = response.data;
        } else {
          profileData = response;
        }
        
        setCurrentUser(prev => ({
          ...prev,
          ...profileData,
          id: prev?.id || prev?._id || profileData._id,
          role: prev?.role || profileData.role || 'doctor'
        }));
        
        console.log('👨‍⚕️ [Profile] Doctor info loaded:', profileData.firstName);
        
      } catch (apiError) {
        console.warn('⚠️ [Profile] Could not fetch fresh profile data:', apiError.message);
      }
      
    } catch (error) {
      console.error('❌ [Profile] Error loading doctor info:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    loadDoctorInfo();
  }, []);

  // 🔧 THEME STYLES: Apply dynamic theme colors
  const themeStyles = useMemo(() => {
    const theme = getThemeStyles();
    
    return {
      '--primary-color': theme.primaryColor || '#A7D7C5',
      '--secondary-color': theme.secondaryColor || '#2196F3'
    };
  }, [getThemeStyles]);

  // Filter menu items based on feature flags
  const menuItems = useMemo(() => {
    return ALL_MENU_ITEMS.filter(item => {
      if (item.alwaysShow) return true;
      if (!isFeatureEnabled || !tenantSettings) return true;
      
      const isEnabled = isFeatureEnabled(item.feature);
      console.log(`🔧 [Menu] ${item.label}: ${isEnabled ? 'SHOWN' : 'HIDDEN'}`);
      return isEnabled;
    });
  }, [isFeatureEnabled, tenantSettings]);

  const handleProfileClick = () => {
    navigate('/doctor/profile');
  };

  // 🔧 PROFILE DISPLAY: Stable profile picture handling
  const getDoctorProfileDisplay = () => {
    if (profileLoading) {
      return (
        <div className="doctor-profile-loading">
          <div className="profile-loading-spinner"></div>
        </div>
      );
    }
    
    if (currentUser?.profilePicture) {
      return (
        <>
          <img 
            src={currentUser.profilePicture} 
            alt="Profile" 
            className="doctor-profile-image"
            onError={(e) => {
              console.warn('❌ [Profile] Image failed to load:', e.target.src);
              e.target.style.display = 'none';
              const initialsDiv = e.target.parentNode.querySelector('.doctor-profile-initials-fallback');
              if (initialsDiv) {
                initialsDiv.style.display = 'flex';
              }
            }}
            onLoad={() => {
              console.log('✅ [Profile] Image loaded successfully');
            }}
          />
          <div className="doctor-profile-initials-fallback" style={{ display: 'none' }}>
            {(currentUser?.firstName || 'D').charAt(0)}{(currentUser?.lastName || 'R').charAt(0)}
          </div>
        </>
      );
    } else {
      const firstName = currentUser?.firstName || 'D';
      const lastName = currentUser?.lastName || 'R';
      return (
        <div className="doctor-profile-initials">
          {firstName.charAt(0)}{lastName.charAt(0)}
        </div>
      );
    }
  };

  if (isLoading) {
    return (
      <div className="doctor-layout" style={themeStyles}>
        <div className="doctor-sidebar">
          <div className="sidebar-header">
            <div className="loading-logo">Loading {platformName}...</div>
          </div>
        </div>
        <div className="doctor-content">
          <div className="loading-content">
            <p>Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="doctor-layout" style={themeStyles}>
      {/* Left Sidebar */}
      <div className="doctor-sidebar">
        <div className="sidebar-header">
          {/* 🔧 SMART LOGO: Dynamic with safeguards */}
          <img 
            src={logoSrc}
            alt={`${platformName} Logo`} 
            className="doctor-logo" 
            onLoad={handleLogoLoad}
            onError={handleLogoError}
            style={{ 
              opacity: 1,
              transition: 'opacity 0.3s ease'
            }}
          />
        </div>
        
        <div className="sidebar-menu">
          <ul className="nav-menu">
            {menuItems.map(item => (
              <li key={item.id} className="nav-item">
                <NavLink 
                  to={item.path}
                  className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
                  end={item.path === '/doctor'}
                  title={item.label}
                  onMouseEnter={(e) => handleMouseEnter(e, item.id)}
                  onMouseLeave={handleMouseLeave}
                  style={{
                    opacity: item.implemented ? 1 : 0.6,
                    borderColor: 'var(--primary-color)'
                  }}
                >
                  <item.icon className="nav-icon" />
                  <span className="nav-label">{item.label}</span>
                  {!item.implemented && (
                    <span className="coming-soon-badge">Soon</span>
                  )}
                  
                  {/* ✅ DYNAMIC TOOLTIP with calculated position */}
                  {hoveredItem === item.id && (
                    <span 
                      className="nav-tooltip"
                      style={{ top: `${tooltipPosition.top}px` }}
                    >
                      {item.label}
                    </span>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Profile Button */}
        <div className="sidebar-footer">
          <div 
            className={`doctor-profile-button ${profileLoading ? 'loading' : ''}`}
            onClick={handleProfileClick}
            title={`${currentUser?.firstName} ${currentUser?.lastName} - View Profile`}
            onMouseEnter={(e) => handleMouseEnter(e, 'profile')}
            onMouseLeave={handleMouseLeave}
            style={{
              borderColor: 'var(--primary-color)',
              backgroundColor: 'var(--primary-color)10'
            }}
          >
            <div className="profile-picture-container">
              {getDoctorProfileDisplay()}
            </div>
            
            {/* ✅ DYNAMIC TOOLTIP for profile */}
            {hoveredItem === 'profile' && (
              <span 
                className="nav-tooltip"
                style={{ top: `${tooltipPosition.top}px` }}
              >
                Profile Settings
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* Main Content Area */}
      <div className="doctor-content">
        <div className="doctor-content-container">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default DoctorLayout;